import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { FeatureFlagService } from '@trimble-gcs/feature-flags';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { filter, switchMap } from 'rxjs';
import { ConnectService } from '../../../connect/connect.service';
import { DialogComponent } from '../../../dialog/dialog.component';
import { CANCEL_BUTTON, DialogData } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { ClearError } from '../../../error-handling/error.actions';
import { FeatureFlagKey } from '../../../feature-flags/feature-flag-key';
import { LoadingService } from '../../../loading/loading.service';
import { ScandataRecycleService } from '../../../scandata/recycle/scandata-recycle.service';
import { UnselectScandataModel } from '../../../scandata/scandata.actions';
import { PointcloudType, ScandataModel } from '../../../scandata/scandata.models';
import { openInNumark } from '../../../utils/open-in-numark';
import { SetInfoView, SetPreviewVisible } from '../../options-panel.actions';
import { OptionsPanelInfoView } from '../../options-panel.models';
import { OptionsPanelState } from '../../options-panel.state';
import { ClassificationComponent } from './classification/classification.component';
import { PropertiesComponent } from './properties/properties.component';
import { singleSelectedTranslations } from './single-selected.translations';
import { TaggingComponent } from './tagging/tagging.component';
import { ViewerGlbComponent } from './viewer-glb/viewer-glb.component';

interface ViewOption {
  buttonId: string;
  label: string;
  action: () => void;
}

@UntilDestroy()
@Component({
  selector: 'sd-single-selected',
  imports: [
    CommonModule,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    ViewerGlbComponent,
    ClassificationComponent,
    PropertiesComponent,
    TaggingComponent,
    ScrollingModule,
    MatTabsModule,
    MatProgressBarModule,
    MatMenuModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './single-selected.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleSelectedComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly loadingService = inject(LoadingService);
  private readonly scandataRecycleService = inject(ScandataRecycleService);
  private readonly connectService = inject(ConnectService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);
  private readonly featureFlagService =
    inject<FeatureFlagService<FeatureFlagKey>>(FeatureFlagService);

  readonly translations = singleSelectedTranslations();

  readonly scandataModel = input.required<ScandataModel>();
  readonly disableDelete = input(false);
  readonly disableEdit = input(false);

  readonly downloadClicked = output();

  private readonly isSaving = signal(false);
  private readonly isLoading = toSignal(this.loadingService.isLoading$(this));
  readonly showProgressBar = computed(() => this.isSaving() || this.isLoading());

  private readonly showPreview = this.store.selectSignal(OptionsPanelState.previewVisible);
  private readonly hasPreview = this.scanHasPreview();

  readonly previewButtonDisabled = computed(() => !this.hasPreview());
  readonly previewButtonTooltip = this.getPreviewButtonTooltip();
  readonly previewVisible = computed(() => this.hasPreview() && this.showPreview());

  private readonly hasViewInNumarkFeature = toSignal(
    this.featureFlagService.hasFeature$('viewInNumark'),
    {
      initialValue: false,
    },
  );

  private readonly viewIn3DOption: ViewOption = {
    buttonId: 'options-panel_view_in_3d',
    label: this.translations.viewIn3d.text,
    action: () => this.viewIn3d(),
  };
  private readonly viewInNumarkOption: ViewOption = {
    buttonId: 'options-panel_view_in_numark',
    label: this.translations.viewInNumark.text,
    action: () => this.viewInNumark(),
  };
  readonly viewOptions = this.getViewOptions();

  private readonly infoTabs = this.getInfoTabs();
  private readonly infoView = this.store.selectSignal(OptionsPanelState.infoView);
  readonly selectedInfoTabIndex = this.getselectedInfoTabIndex();

  private deleteDialogRef: MatDialogRef<DialogComponent> | null = null;

  ngOnDestroy(): void {
    this.deleteDialogRef?.close();
  }

  togglePreview() {
    const previewVisible = !this.showPreview();
    this.store.dispatch(new SetPreviewVisible(previewVisible));
  }

  infoTabChange(event: MatTabChangeEvent) {
    const tab = Object.keys(this.infoTabs)[event.index] as OptionsPanelInfoView;
    this.store.dispatch([new SetInfoView(tab), new ClearError('scanDetailsSaveError')]);
  }

  viewIn3d() {
    this.connectService.goTo3dExtension();
  }

  viewInNumark() {
    openInNumark(this.store, [this.scandataModel().id]);
  }

  unselect() {
    this.store.dispatch(new UnselectScandataModel(this.scandataModel().id));
  }

  handleSave(saving: boolean) {
    this.isSaving.set(saving);
  }

  deleteScandata() {
    const dialogTitle = this.translationService.translate(
      this.translations.deleteConfirmation.title.text,
      {
        'Reality Capture': 'Reality Capture',
      },
    );
    const dialogData = new DialogData(
      dialogTitle,
      this.translations.deleteConfirmation.message.text,
      { text: this.translations.deleteConfirmation.confirm.text, color: 'danger' },
      CANCEL_BUTTON,
    );

    this.deleteDialogRef = this.dialogService.showMessage(dialogData);

    this.deleteDialogRef
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed ?? false),
        switchMap(() => {
          return this.loadingService.loadFrom(
            this.scandataRecycleService.deleteScan(this.scandataModel()),
            this,
          );
        }),
      )
      .subscribe();
  }

  private scanHasPreview() {
    return computed(() => {
      const previewUrl = this.scandataModel().previewUrl;
      const hasPreview = isDefined(previewUrl) && previewUrl.length > 0;
      return hasPreview;
    });
  }

  private getViewOptions() {
    return computed(() => {
      if (!this.hasViewInNumarkFeature()) return [this.viewIn3DOption];

      if (this.scandataModel().type === PointcloudType.MobileMappingRun) {
        return [this.viewInNumarkOption, this.viewIn3DOption];
      }

      return this.scandataModel().pointCount > 0
        ? [this.viewIn3DOption, this.viewInNumarkOption]
        : [this.viewIn3DOption];
    });
  }

  private getPreviewButtonTooltip() {
    return computed(() => {
      if (!this.hasPreview()) return this.translations.preview.notAvailable.text;

      return this.showPreview()
        ? this.translations.preview.hide.text
        : this.translations.preview.show.text;
    });
  }

  private getInfoTabs() {
    return {
      [OptionsPanelInfoView.Properties]: 0,
      [OptionsPanelInfoView.Tagging]: 1,
      [OptionsPanelInfoView.Classification]: 2,
    };
  }

  private getselectedInfoTabIndex() {
    return computed(() => {
      const infoView = this.infoView();
      return this.infoTabs[infoView];
    });
  }
}
